import React, { useEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import { Modal, Form, Tabs, ConfigProvider, message, Spin, Button } from 'antd';
import { EPracticeType, TCredentialingProvider } from 'types/credentialing';
import useCredentialProviders from 'hooks/credentialing/useCredentialProviders';
import { renderCredStep, type CredStepKey } from '../../steps';
import { CredTargetProviderContext } from '../../steps/targetProvider';
import { focusTab, isPayerFocus, resolveFocusName, type TActionFocus } from '../../actionFocus';
import {
    REVIEW_SECTIONS,
    getSectionErrors,
    getSectionMissingFields,
    fieldMarkableErrors,
    renderSectionView,
} from '../../reviewSpec';
import {
    saveStepValues,
    fetchProviderCredValuesByPanel,
    type OverrideProviderId,
} from '../../credReviewData';
import { markCredInfoUpdateConfirmed, isCredInfoUpdateConfirmedRecently } from 'utils/localstore';
import useCurrentUser from 'hooks/useCurrentUser';
import ProviderStore from 'store/Provider';
import {
    ProviderSignatureField,
    ProviderSignatureView,
} from 'components/CredentialingModal/components/AgreementStep/SignatureBlock';
import SectionHeader from '../SectionHeader';
import ps from '../../s.module.less';
import { isGroupAdmin } from 'utils/common';
import ReviewSection from '../ReviewSection';
import PracticeEnrollmentSection from '../PracticeEnrollmentSection';
import MedicaidGroupSection from '../MedicaidGroupSection';
import PayerRequirementsPanel from '../PayerRequirementsPanel';
import PayoutAccountSection from '../PayoutAccountSection';
import ContractsSection from '../ContractsSection';
import s from './s.module.less';

// V2 Settings 新增 tab（对齐原型 VALID_TABS：practice-enroll 最前、payer-info 在向导 tab 后、payout 最后）
const TAB_PRACTICE_ENROLL = 'practice-enroll';
const TAB_PAYER_REQ = 'payer-info';
const TAB_CONTRACTS = 'contracts';
const TAB_PAYOUT = 'payout';
// practice 级 tab 不随 provider 切换器（切换器只在 provider 级 tab 显示）
const PRACTICE_LEVEL_TABS: string[] = [TAB_PRACTICE_ENROLL, TAB_CONTRACTS, TAB_PAYOUT];

interface Props {
    open: boolean;
    onClose: () => void;
    onSaveSuccess?: () => void;
    initialValues?: Record<string, any>;
    providerId?: OverrideProviderId;
    practiceType?: EPracticeType;
    // 支持向导 7 步 key 或新 tab key（practice-enroll / payer-info / payout）
    initialTab?: string;
    // action needed 深链的字段级定位（ctaPayload 协议）：向导 tab 自动进编辑并滚动标红目标字段，
    // payer-info tab 透传给 PayerRequirementsPanel 处理
    initialFocus?: TActionFocus | null;
    initialProviderId?: number;
    panelIdByProvider?: Record<number, number>;
    // 有「进行中」payer 的 providerId 集合；命中则保存前先弹更新确认框
    inProgressProviderIds?: Set<number>;
}

const providerLabel = (p: TCredentialingProvider) =>
    `${p.firstName || ''} ${p.lastName || ''}`.trim() || p.email || `Provider ${p.providerId}`;

const BannerIcon = () => (
    <svg viewBox="0 0 20 20" fill="currentColor" width="18" height="18" aria-hidden>
        <path
            fillRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 10a1 1 0 100-2 1 1 0 000 2z"
            clipRule="evenodd"
        />
    </svg>
);

export default function CredentialingInfoModal({
    open,
    onClose,
    onSaveSuccess,
    initialValues,
    providerId,
    practiceType,
    initialTab,
    initialFocus,
    initialProviderId,
    panelIdByProvider,
    inProgressProviderIds,
}: Props) {
    const [form] = Form.useForm();
    const [activeTab, setActiveTab] = useState<string>(initialTab || REVIEW_SECTIONS[0].key);
    const [editingKey, setEditingKey] = useState<CredStepKey | null>(null);
    const [saving, setSaving] = useState(false);
    // 待确认更新的 section：非 null 时展示「Update your credentialing info?」确认框
    const [confirmKey, setConfirmKey] = useState<CredStepKey | null>(null);
    // 保存校验未通过时，顶部汇总列出该 section 缺失/不合理的字段（对齐 Review 页 banner，不用 toast）
    const [errorList, setErrorList] = useState<string[]>([]);
    const snapshotRef = useRef<Record<string, any> | null>(null);
    const scrollRef = useRef<HTMLDivElement>(null);
    // 上一次保存标红的字段名，下次保存前先清掉，避免已填好的字段仍残留红色
    const erroredFieldsRef = useRef<any[]>([]);

    const clearFieldErrors = () => {
        if (erroredFieldsRef.current.length) {
            form.setFields(erroredFieldsRef.current.map((name) => ({ name, errors: [] })));
            erroredFieldsRef.current = [];
        }
    };

    const isGroup = practiceType === EPracticeType.GROUP;

    // Payout 是 practice 级账户：只对 group practice 下的 team provider（非 owner/manager）隐藏。
    // 不能用 isGroup（billingNpiType）判定——billing 选 PRACTICE_NPI 但 groupPractice=false 的账号会被误伤。
    const [currentUser] = useCurrentUser();
    const canSeePayout = !(currentUser?.groupPractice && !isGroupAdmin(currentUser));

    const isWizardTab = REVIEW_SECTIONS.some((sec) => sec.key === activeTab);
    const tabItems = [
        ...(isGroup ? [{ key: TAB_PRACTICE_ENROLL, label: 'Practice enrollment' }] : []),
        ...REVIEW_SECTIONS.map((sec) => ({ key: sec.key as string, label: sec.title })),
        { key: TAB_PAYER_REQ, label: 'Payer requirements' },
        { key: TAB_CONTRACTS, label: 'Contracts' },
        ...(canSeePayout ? [{ key: TAB_PAYOUT, label: 'Payout account' }] : []),
    ];

    // group 模式：弹窗内部自取机构 provider 列表，只展示已完成 onboarding 的 provider
    const [allProviders] = useCredentialProviders();
    const groupProviders = useMemo(
        () => (isGroup ? allProviders.filter((p) => p.onboardingStatus === 'COMPLETED') : []),
        [isGroup, allProviders],
    );

    // 选中 provider + 各 provider 的 values 缓存（懒加载）
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [valuesByProvider, setValuesByProvider] = useState<Record<number, Record<string, any>>>({});
    const [loadingProvider, setLoadingProvider] = useState(false);

    // 列表就绪后默认选中第一个（弹窗关闭时重置，便于下次打开重新落位）
    useEffect(() => {
        if (!open || !isGroup) {
            if (!open) {
                setSelectedId(null);
                setActiveTab(initialTab || REVIEW_SECTIONS[0].key);
            }
            return;
        }
        if (selectedId == null && groupProviders.length) {
            const targetId =
                initialProviderId && groupProviders.some((p) => p.providerId === initialProviderId)
                    ? initialProviderId
                    : groupProviders[0].providerId;
            setSelectedId(targetId);
        }
    }, [open, isGroup, groupProviders, selectedId, initialProviderId, initialTab]);

    // 选中即加载：命中缓存则直接灌值，否则拉取该 provider 的 7 步数据后灌值
    useEffect(() => {
        if (!open || !isGroup || selectedId == null) return undefined;
        const cached = valuesByProvider[selectedId];
        if (cached) {
            form.resetFields();
            form.setFieldsValue(cached);
            return undefined;
        }
        let cancelled = false;
        setLoadingProvider(true);
        (async () => {
            const vals = await fetchProviderCredValuesByPanel(panelIdByProvider?.[selectedId], selectedId).catch(
                () => ({}),
            );
            if (cancelled) return;
            setValuesByProvider((prev) => ({ ...prev, [selectedId]: vals }));
            form.resetFields();
            form.setFieldsValue(vals);
            setLoadingProvider(false);
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, isGroup, selectedId]);

    const switchProvider = (id: number) => {
        if (id === selectedId) return;
        snapshotRef.current = null;
        setEditingKey(null);
        clearFieldErrors();
        setErrorList([]);
        setSelectedId(id);
    };

    // 只读视图数据源：
    // - group：直接用选中 provider 的缓存（valuesByProvider）。切 provider 时随 selectedId 触发 re-render
    //   立即取到正确缓存；不能合并 form.getFieldsValue()——form 由 effect 在 render 后异步 setFieldsValue，
    //   命中缓存分支又不 setState、不触发父组件 re-render，读 form 会停留在上一个 provider 的数据。
    //   编辑中的 section 走 Step 表单（读 Form context），保存后会同步回写缓存，故只读态用缓存即可。
    // - solo：单 provider、initialValues 由父组件静态预取，合并 form 以反映本次已保存的编辑
    //   （saveEdit 后 setEditingKey(null) 会触发 re-render 重算）。
    const groupBase = selectedId != null ? valuesByProvider[selectedId] : undefined;
    const values = isGroup ? { ...(groupBase || {}) } : { ...(initialValues || {}), ...form.getFieldsValue(true) };

    // 当前编辑/保存作用的 providerId：group 用选中项，solo 用父组件传入
    const effectiveProviderId: OverrideProviderId = isGroup ? (selectedId ?? undefined) : providerId;

    // Verification 的签名（10/26）：group 切到选中成员名下取 / 存；solo 是登录者本人
    const [getUser] = ProviderStore.useStore();
    const loginProviderId = getUser('data')?.id;
    const sigProviderId = isGroup && selectedId != null ? selectedId : undefined;
    const sigSelf = sigProviderId == null || String(sigProviderId) === String(loginProviderId);
    const sigBindId: number | undefined = sigProviderId ?? (loginProviderId != null ? Number(loginProviderId) : undefined);
    const sigName = (() => {
        const p = groupProviders.find((g) => g.providerId === selectedId);
        return p ? providerLabel(p) : '';
    })();

    // initialValues 是异步拉取的（打开后才到达），Form 只在挂载时读一次 initialValues，
    // 因此数据到达后需手动回填，避免用 key 强制 remount 造成弹窗动画播两次。
    // 仅 solo：group 由内部按 selectedId 逐 provider 拉取回填，若在此写入 initialValues（父组件按
    // owner/当前 provider 预取）会污染选中 provider 的表单，导致切 tab 显示串数据。
    useEffect(() => {
        if (open && !isGroup && initialValues) form.setFieldsValue(initialValues);
    }, [open, isGroup, initialValues, form]);

    useEffect(() => {
        if (open && initialTab) setActiveTab(initialTab);
    }, [open, initialTab]);

    const openEdit = (key: CredStepKey) => {
        snapshotRef.current = form.getFieldsValue(true);
        clearFieldErrors();
        setErrorList([]);
        setEditingKey(key);
    };

    // ===== action needed 字段级定位（ctaPayload 协议）=====
    // 向导 tab：values 就位后自动进入该 section 编辑，字段挂载后按 fieldKey 解析 NamePath
    // 滚动并标红（reason 作为报错文案）。解析失败（refId 找不到行等）退化为只展开编辑态。
    const focusDoneRef = useRef(false);
    useEffect(() => {
        if (open) focusDoneRef.current = false;
    }, [open, initialFocus]);

    useEffect(() => {
        if (!open || !initialFocus || focusDoneRef.current) return undefined;
        const tab = focusTab(initialFocus);
        if (!tab || tab !== activeTab || !REVIEW_SECTIONS.some((sec) => sec.key === activeTab)) return undefined;
        if (editingKey !== activeTab) {
            // 等该 provider 的 values 就位再进编辑，否则快照/回填会拿到空表单
            const ready = isGroup ? selectedId != null && !!valuesByProvider[selectedId] : initialValues !== undefined;
            if (ready) openEdit(activeTab as CredStepKey);
            return undefined;
        }
        const timer = window.setTimeout(() => {
            focusDoneRef.current = true;
            const name = resolveFocusName(initialFocus, form.getFieldsValue(true));
            if (!name) return;
            form.scrollToField(name, { behavior: 'smooth', block: 'center' });
            form.setFields([{ name, errors: [initialFocus.reason || 'This field needs an update.'] }]);
            // 记入待清列表：下次保存前先清红，避免已改好的字段残留报错
            erroredFieldsRef.current = [name];
        }, 300);
        return () => window.clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initialFocus, activeTab, editingKey, isGroup, selectedId, valuesByProvider, initialValues]);
    const cancelEdit = () => {
        if (snapshotRef.current) form.setFieldsValue(snapshotRef.current);
        clearFieldErrors();
        setErrorList([]);
        setEditingKey(null);
    };
    // 实际保存当前编辑的 section：取本步骤字段序列化后调对应接口（与向导 saveStep 一致）
    const performSave = async (key: CredStepKey) => {
        setSaving(true);
        try {
            const ok = await saveStepValues(key, form, effectiveProviderId);
            if (!ok) throw new Error('save failed');
            snapshotRef.current = form.getFieldsValue(true);
            // 同步缓存，切走再切回仍保留本次编辑
            if (isGroup && selectedId != null) {
                setValuesByProvider((prev) => ({ ...prev, [selectedId]: form.getFieldsValue(true) }));
            }
            setEditingKey(null);
            message.success('Changes saved.');
            onSaveSuccess?.();
        } catch (e) {
            message.error('Failed to save changes. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    // 点 Save changes：先按该步骤规则校验，通过后——若当前 provider 有进行中的 payer 且 24h 内未确认过，
    // 先弹更新确认框；否则直接保存。
    const requestSave = (key: CredStepKey) => {
        // 校验本 section 全部必填/格式项：标红每个缺失/不合理字段（含无内建 rule 的上传、下拉），
        // 顶部汇总列出缺哪些（对齐 Review 页 banner），并滚动到顶部让 banner 可见
        const values2 = form.getFieldsValue(true);
        const sectionErrors = getSectionMissingFields(values2, key);
        // 先清掉上次标红的字段，避免已填好的字段残留红色
        clearFieldErrors();
        if (sectionErrors.length > 0) {
            // 标红剔除 NpiRegistryBox 已内联展示的错误（顶部汇总仍会列出），避免上下两行重复红字
            const markable = fieldMarkableErrors(sectionErrors);
            form.setFields(markable);
            erroredFieldsRef.current = markable.map((e) => e.name);
            setErrorList(getSectionErrors(values2, key));
            scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }
        setErrorList([]);
        const hasInProgressPayer = effectiveProviderId != null && !!inProgressProviderIds?.has(effectiveProviderId);
        if (hasInProgressPayer && !isCredInfoUpdateConfirmedRecently()) {
            setConfirmKey(key);
            return;
        }
        performSave(key);
    };

    // 点确认框 Update：记录 24h 抑制标记后保存
    const confirmSave = () => {
        if (confirmKey == null) return;
        markCredInfoUpdateConfirmed();
        const key = confirmKey;
        setConfirmKey(null);
        performSave(key);
    };

    return (
        <ConfigProvider
            theme={{ token: { colorPrimary: '#0E62F4' } }}
            getPopupContainer={(node) => (node?.closest('.ant-modal-body') as HTMLElement) || document.body}
        >
            <Modal
                open={open}
                onCancel={onClose}
                footer={null}
                title={null}
                width={1040}
                centered
                destroyOnClose
                className={s.infoModal}
            >
                {/* 头部在滚动区外固定：滚动时 Settings 标题常驻，编辑态吸顶的 section 标题行贴其下方 */}
                <div className={s.modalHeader}>
                    <h3 className={s.modalTitle}>Settings</h3>
                    <p className={s.modalSubtitle}>
                        View and update your credentialing information. These are the same details you filled during
                        onboarding.
                    </p>
                </div>
                <div className={s.modalScroll} ref={scrollRef}>
                    {/* Tabs 仅作导航（items 不带 children）：practice 级 tab 不属于任何 provider，
                        provider 切换器只在 provider 级 tab 下方出现，内容区随 activeTab 自渲染 */}
                    <Tabs activeKey={activeTab} onChange={setActiveTab} className={s.infoTabs} items={tabItems} />
                    {isGroup && !PRACTICE_LEVEL_TABS.includes(activeTab) && (
                        <div className={s.providerBar}>
                            <div className={s.providerBarLabel}>Select a provider to view or edit</div>
                            <div className={s.providerChips}>
                                {groupProviders.map((p) => (
                                    <button
                                        type="button"
                                        key={p.providerId}
                                        className={cx(
                                            s.providerChip,
                                            p.providerId === selectedId && s.providerChipActive,
                                        )}
                                        onClick={() => switchProvider(p.providerId)}
                                    >
                                        {providerLabel(p)}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {activeTab === TAB_PRACTICE_ENROLL && (
                        <>
                            <PracticeEnrollmentSection actionsInHeader />
                            <MedicaidGroupSection actionsInHeader />
                        </>
                    )}

                    {activeTab === TAB_CONTRACTS && <ContractsSection isGroup={isGroup} />}

                    {activeTab === TAB_PAYOUT && <PayoutAccountSection />}

                    {activeTab === TAB_PAYER_REQ && (
                        <PayerRequirementsPanel
                            providerId={effectiveProviderId}
                            actionsInHeader
                            initialFocus={initialFocus && isPayerFocus(initialFocus) ? initialFocus : undefined}
                        />
                    )}

                    {isWizardTab && (
                        <Spin spinning={loadingProvider}>
                            <CredTargetProviderContext.Provider value={effectiveProviderId}>
                                <Form
                                    form={form}
                                    layout="vertical"
                                    requiredMark={false}
                                    initialValues={isGroup ? undefined : initialValues}
                                    preserve
                                >
                                    {editingKey === activeTab && errorList.length > 0 && (
                                        <div className={s.errorBanner}>
                                            <BannerIcon />
                                            <div>
                                                <div className={s.errorBannerTitle}>
                                                    Please complete the following before saving
                                                </div>
                                                <ul>
                                                    {errorList.map((e) => (
                                                        <li key={e}>{e}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    )}
                                    {REVIEW_SECTIONS.filter((sec) => sec.key === activeTab).map((sec) => (
                                        <ReviewSection
                                            key={sec.key}
                                            sectionKey={sec.key}
                                            title={sec.title}
                                            values={values}
                                            editing={editingKey === sec.key}
                                            saving={saving}
                                            actionsInHeader
                                            onEdit={() => openEdit(sec.key)}
                                            onCancel={cancelEdit}
                                            onSave={() => requestSave(sec.key)}
                                            // Verification 末尾多一个 Signature（读回 + 编辑态的签名区块），只在 Settings
                                            viewContent={
                                                sec.key === 'final' ? (
                                                    <>
                                                        {renderSectionView('final', values)}
                                                        <div className={ps.reviewRow} style={{ marginTop: 16 }}>
                                                            <div className={ps.reviewItem}>
                                                                <div className={ps.reviewItemLabel}>Signature</div>
                                                                <ProviderSignatureView providerId={sigProviderId} />
                                                            </div>
                                                        </div>
                                                    </>
                                                ) : undefined
                                            }
                                        >
                                            {renderCredStep(sec.key)}
                                            {/* 绑定签名（#20）只有 practice owner / admin 能调，与 Payout tab 同一判断 */}
                                            {sec.key === 'final' && canSeePayout && sigBindId != null && (
                                                <div className={ps.wizSection}>
                                                    <SectionHeader title="Signature" />
                                                    <ProviderSignatureField
                                                        key={String(sigBindId)}
                                                        bindProviderId={sigBindId}
                                                        overrideProviderId={sigProviderId}
                                                        name={sigName}
                                                        self={sigSelf}
                                                    />
                                                </div>
                                            )}
                                        </ReviewSection>
                                    ))}
                                </Form>
                            </CredTargetProviderContext.Provider>
                        </Spin>
                    )}
                </div>
            </Modal>
            <Modal
                open={confirmKey != null}
                onCancel={() => setConfirmKey(null)}
                footer={null}
                title={null}
                width={440}
                centered
                maskClosable={!saving}
                closable={false}
                className={s.confirmModal}
            >
                <h4 className={s.confirmTitle}>Update your credentialing info?</h4>
                <p className={s.confirmBody}>
                    If an application hasn&apos;t gone to a payer yet, it will use your new details. If it&apos;s
                    already with a payer, that review keeps what you submitted, and your new details will be used the
                    next time you credential.
                </p>
                <div className={s.confirmActions}>
                    <Button onClick={() => setConfirmKey(null)} disabled={saving}>
                        Cancel
                    </Button>
                    <Button type="primary" onClick={confirmSave} loading={saving}>
                        Update
                    </Button>
                </div>
            </Modal>
        </ConfigProvider>
    );
}
